import React, { useEffect, useState } from "react"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import TextField from "@mui/material/TextField"
import Button from "@mui/material/Button"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TableContainer from "@mui/material/TableContainer"
import Paper from "@mui/material/Paper"
import IconButton from "@mui/material/IconButton"
import DeleteIcon from "@mui/icons-material/Delete"
import EditIcon from "@mui/icons-material/Edit"
import { useSnackbar } from "notistack"
import { listBadges, createBadge, updateBadge, deleteBadge } from "services/badge"
import { verifyResponse } from "utils/httpRequest"
import useMediaManager from "components/common/MediaManager"
import PageHeading from "components/common/LayoutComponents/PageHeading"
import PageContent from "components/common/LayoutComponents/PageContent"
import { UseConfirmDialog } from "components/customHooks"
import { MEDIA_TYPE_INDEX } from "constants/global"
import { StyledBadges } from "./Badges.style"

export default function Badges() {
  const { enqueueSnackbar } = useSnackbar()
  const [loading, setLoading] = useState(false)
  const [badges, setBadges] = useState([])
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState({ key: "", name: "", description: "", iconUrl: "", color: "" })
  const [openMediaManager, MediaManagerContent] = useMediaManager()
  const [ConfirmDialog, openConfirmDialog] = UseConfirmDialog({
    title: "Delete Badge",
    description: "Are you sure you want to delete this badge?",
    agreeText: "Delete",
    disagreeText: "Cancel",
  })

  const BADGE_ICON_RECOMMENDED = "128×128 (square). Displayed at ~16–18px in the app."
  const BADGE_ICON_MAX = 256

  const validateImageMaxDimensions = (url, maxPx) =>
    new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        const w = img.naturalWidth || img.width || 0
        const h = img.naturalHeight || img.height || 0
        resolve({ width: w, height: h, ok: w > 0 && h > 0 && w <= maxPx && h <= maxPx })
      }
      img.onerror = () => reject(new Error("Image failed to load"))
      img.src = url
    })

  const handleMediaSelect = async (selectedMedia) => {
    if (!selectedMedia || !selectedMedia.url) return
    const url = selectedMedia.url
    try {
      const { width, height, ok } = await validateImageMaxDimensions(url, BADGE_ICON_MAX)
      if (!ok) {
        enqueueSnackbar(
          `Badge icon must be ≤ ${BADGE_ICON_MAX}×${BADGE_ICON_MAX}px. Selected image is ${width || "?"}×${height || "?"}px.`,
          { variant: "error" }
        )
        return
      }
      setForm((prev) => ({ ...prev, iconUrl: url }))
      enqueueSnackbar("Badge icon selected", { variant: "success" })
    } catch (e) {
      enqueueSnackbar(
        `Could not verify image dimensions. Please upload an image ≤ ${BADGE_ICON_MAX}×${BADGE_ICON_MAX}px.`,
        { variant: "error" }
      )
    }
  }

  const handleOpenMediaManager = () => {
    openMediaManager(handleMediaSelect, [MEDIA_TYPE_INDEX.image], "search")
  }

  const load = async () => {
    setLoading(true)
    try {
      const resp = await listBadges()
      verifyResponse(resp)
      setBadges(resp.data?.list || resp.list || [])
    } catch (e) {
      enqueueSnackbar(`Failed to load badges: ${e.message || e}`, { variant: "error" })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const resetForm = () => {
    setEditing(null)
    setForm({ key: "", name: "", description: "", iconUrl: "", color: "" })
  }

  const onSubmit = async () => {
    const key = (form.key || "").trim()
    const name = (form.name || "").trim()
    if (!key || !name) {
      enqueueSnackbar("Badge key and name are required", { variant: "warning" })
      return
    }
    setLoading(true)
    try {
      if (editing?.id || editing?._id) {
        const resp = await updateBadge({ id: editing.id || editing._id, ...form, key, name })
        verifyResponse(resp)
        enqueueSnackbar("Badge updated", { variant: "success" })
      } else {
        const resp = await createBadge({ ...form, key, name })
        verifyResponse(resp)
        enqueueSnackbar("Badge created", { variant: "success" })
      }
      resetForm()
      await load()
    } catch (e) {
      enqueueSnackbar(`Failed to save badge: ${e.message || e}`, { variant: "error" })
    } finally {
      setLoading(false)
    }
  }

  const startEdit = (b) => {
    setEditing(b)
    setForm({
      key: b.key || "",
      name: b.name || "",
      description: b.description || "",
      iconUrl: b.iconUrl || "",
      color: b.color || "",
    })
  }

  const onDelete = async (b) => {
    openConfirmDialog(async () => {
      setLoading(true)
      try {
        const resp = await deleteBadge({ id: b.id || b._id })
        verifyResponse(resp)
        enqueueSnackbar("Badge deleted", { variant: "success" })
        await load()
      } catch (e) {
        enqueueSnackbar(`Failed to delete badge: ${e.message || e}`, { variant: "error" })
      } finally {
        setLoading(false)
      }
    })
  }

  const title = editing ? `Edit Badge: ${editing.name || editing.key}` : "Create Badge"

  return (
    <PageContent>
      <StyledBadges>
        <PageHeading>
          <div className="heading-copy">
            <h1 className="page-title">Badges</h1>
            <p className="page-subtitle">
              Create and manage achievement badges displayed across member experiences.
            </p>
          </div>
        </PageHeading>

        <div className="page-surface">
          <Card className="form-card">
            <CardContent>
              <h3 className="section-title">{title}</h3>
              <p className="section-subtitle">
                Define badge metadata, icon assets, and hover description content.
              </p>

              <div className="form-row">
                <TextField
                  label="Key"
                  value={form.key}
                  size="small"
                  onChange={(e) => setForm({ ...form, key: e.target.value })}
                />
                <TextField
                  label="Name"
                  value={form.name}
                  size="small"
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
                <TextField
                  label="Color (hex)"
                  value={form.color}
                  size="small"
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                />
              </div>

              <div className="icon-row">
                <TextField
                  className="icon-field"
                  label="Badge Icon URL"
                  value={form.iconUrl}
                  size="small"
                  InputProps={{ readOnly: true }}
                  helperText={`Recommended: ${BADGE_ICON_RECOMMENDED} • Max: ${BADGE_ICON_MAX}×${BADGE_ICON_MAX}px`}
                />
                <Button variant="contained" onClick={handleOpenMediaManager} disabled={loading}>
                  Select / Upload Badge Icon
                </Button>
                {form.iconUrl ? (
                  <div className="icon-preview">
                    <img
                      src={form.iconUrl}
                      alt="Badge icon preview"
                      onError={(e) => {
                        e.currentTarget.style.display = "none"
                      }}
                    />
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => setForm((prev) => ({ ...prev, iconUrl: "" }))}
                      disabled={loading}
                    >
                      Clear Icon
                    </Button>
                  </div>
                ) : null}
              </div>

              <TextField
                label="Description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                fullWidth
                size="small"
                multiline
                minRows={2}
                sx={{ mb: 2 }}
                helperText="Displayed when users hover over a badge."
              />
              <div className="form-actions">
                <Button variant="contained" disabled={loading} onClick={onSubmit}>
                  {editing ? "Save" : "Create"}
                </Button>
                <Button variant="outlined" disabled={loading} onClick={resetForm}>
                  Clear
                </Button>
              </div>
            </CardContent>
          </Card>

          {MediaManagerContent}

          <Card className="table-card">
            <CardContent>
              <h3 className="section-title">Existing Badges</h3>
              <p className="section-subtitle">
                Current badge catalog used across member profiles and UI markers.
              </p>
              <div className="table-shell">
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Key</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Color</TableCell>
                        <TableCell>Icon</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {badges.map((b) => (
                        <TableRow key={b.id || b._id}>
                          <TableCell>{b.key}</TableCell>
                          <TableCell>{b.name}</TableCell>
                          <TableCell>{b.color || "—"}</TableCell>
                          <TableCell>
                            {b.iconUrl ? (
                              <a href={b.iconUrl} target="_blank" rel="noreferrer">
                                Open Icon
                              </a>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <div className="table-actions">
                              <IconButton onClick={() => startEdit(b)} size="small">
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                onClick={() => onDelete(b)}
                                size="small"
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {badges.length === 0 && (
                        <TableRow>
                          <TableCell className="table-empty" colSpan={5}>
                            No badges configured yet.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {ConfirmDialog}
      </StyledBadges>
    </PageContent>
  )
}

